import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import {
  MultiSelectField,
  TextField,
  validations,
} from '@unite-us/app-components';
import { InfoPanel } from '@unite-us/client-utils';
import _ from 'lodash';
import {
  invalidServiceTypeValidation,
  serviceTypeRequiredValidation,
} from 'common/utils/ServiceTypes/validations';
import { CFR_PART_2_CASE_MESSAGE, REQUEST_SERVICE_INFO_MESSAGE } from 'common/messageConstants';
import { Spinner } from 'common/spinners';
import { ORG_CASE } from 'src/components/Cases/constants';
import CaseOONGroupsSelector from 'src/components/Cases/CaseOONGroupsSelector';
import ClientEnrolledNotification from 'src/components/Referrals/ReferralStatus/components/ClientEnrolledNotification';
import DateDiv from 'src/components/Referrals/ReferralStatus/components/DateDiv';
import CaseNetworkField from './CaseNetworkField';
import CaseProgramField from './CaseProgramField';
import CaseTypeField from './CaseTypeField';
import CaseServiceTypeField from './CaseServiceTypeField';
import CasePrimaryWorkerField from './CasePrimaryWorkerField';

const getCaseTypeValue = (values) => values?.case_type?.type || values?.case_type;

const runServiceTypeValidations = (serviceTypeValidations, value) => {
  for (const validation of serviceTypeValidations) {
    if (typeof validation?.func === 'function') {
      const error = validation.func(value);
      if (error) {
        return error;
      }
    }
  }
  return undefined;
};

const getSelectedOONGroups = (legacyFields) => (
  (legacyFields?.service_case?.oonCase?.selected || [])
    .map((selected) => selected.group.value)
    .filter((selectedValue) => selectedValue?.id)
);

const getOONGroupsFieldValues = (legacyFields) => (
  (legacyFields?.service_case?.oonGroups || []).map((org) => org.group.value)
);

const shouldHideOONGroupsSelector = ({
  fetchingOONGroups,
  defaultProgramSelected,
  values,
}) => (
  fetchingOONGroups ||
  !defaultProgramSelected ||
  !values?.service_case?.service_type?.id ||
  !values?.network
);

const resolveOONGroups = ({
  canPaginateNetworkGroups,
  oonGroups,
  selectedOONGroups,
  oonGroupsFieldValues,
}) => (
  canPaginateNetworkGroups ?
    _.uniqBy([...oonGroups, ...selectedOONGroups], 'id') :
    oonGroupsFieldValues
);

export const CaseTypeAndNetworkSection = ({
  disableOnEmptyCaseType,
  form,
  values,
  group,
  hasGroupLicense,
  hasNetworkLicense,
  networks,
  onCaseTypeChange,
  onNetworkChange,
}) => (
  <>
    {hasGroupLicense && (
      <div className="mb-5">
        <Field
          name="case_type"
          validate={(val) => validations.isRequired(val)}
        >
          {({ input, meta }) => (
            <CaseTypeField
              input={input}
              meta={meta}
              group={group}
              hasGroupLicense={hasGroupLicense}
              hasNetworkLicense={hasNetworkLicense}
              onChange={() => onCaseTypeChange(form)}
            />
          )}
        </Field>
      </div>
    )}

    <div className="mb-5">
      <Field
        name="network"
        validate={(val) => (
          getCaseTypeValue(values) === ORG_CASE ?
            undefined :
            validations.isRequired(val)
        )}
      >
        {({ input, meta }) => (
          <CaseNetworkField
            caseType={getCaseTypeValue(values)}
            disabled={disableOnEmptyCaseType}
            input={input}
            meta={meta}
            networks={networks}
            onChange={() => onNetworkChange(form, values)}
          />
        )}
      </Field>
    </div>
  </>
);

export const ProgramSection = ({
  disableOnEmptyCaseType,
  form,
  values,
  groupId,
  hasGroupLicense,
  isCoordinationGroup,
  programmingOptionsArray,
  onProgramChange,
  user,
  styles,
  caseProgramEnrollment,
  isCheckingProgramEnrollment,
  registerField,
}) => (
  <>
    <div className="add-case-details__program">
      <Field
        name="program"
        validate={(val) => validations.isRequired(val)}
      >
        {({ input, meta }) => (
          <CaseProgramField
            disabled={disableOnEmptyCaseType}
            input={input}
            meta={meta}
            values={values}
            groupId={groupId}
            hasGroupLicense={hasGroupLicense}
            isCoordinationGroup={isCoordinationGroup}
            inline={false}
            placeholder="Choose one..."
            searchPlaceholderValue="Search..."
            programmingOptionsArray={programmingOptionsArray}
            onChange={(value) => onProgramChange(value, form, values)}
            required
            user={user}
            className="mr-5"
          />
        )}
      </Field>

      {values?.program_entry && (
        <Field name="program_entry">
          {({ input }) => (
            <DateDiv
              id="program-entry"
              iconColor={styles.iconTrash.svg.color}
              programEnrollment={caseProgramEnrollment}
              checkingEnrollment={isCheckingProgramEnrollment}
              entryDate={input}
              label="Enrolled"
              registerField={registerField}
            />
          )}
        </Field>
      )}
    </div>

    <div className="add-case-details__program_text">
      <ClientEnrolledNotification
        checkingEnrollment={isCheckingProgramEnrollment}
        programEnrollment={caseProgramEnrollment}
      />
    </div>
  </>
);

export const OONGroupsSection = ({
  canPaginateNetworkGroups,
  contact,
  debouncedSearchNetworkGroups,
  defaultProgramSelected,
  fetchingOONGroups,
  legacyFields,
  onServiceTypeChangeFetchEmpty,
  oonGroups,
  originCoordinates,
  registerField,
  removeSelectedBrowseGroup,
  toggleBrowse,
  values,
}) => {
  if (fetchingOONGroups) {
    return <Spinner scale={0.7} />;
  }

  const selectedOONGroups = getSelectedOONGroups(legacyFields);
  const oonGroupsFieldValues = getOONGroupsFieldValues(legacyFields);
  const hide = shouldHideOONGroupsSelector({
    fetchingOONGroups,
    defaultProgramSelected,
    values,
  });
  const resolvedOONGroups = resolveOONGroups({
    canPaginateNetworkGroups,
    oonGroups,
    selectedOONGroups,
    oonGroupsFieldValues,
  });

  return (
    <CaseOONGroupsSelector
      contact={contact}
      debouncedSearchNetworkGroups={(search) => debouncedSearchNetworkGroups(search, legacyFields)}
      fields={legacyFields}
      hide={hide}
      isOONCase={defaultProgramSelected}
      oonGroups={resolvedOONGroups}
      originCoordinates={originCoordinates}
      network={values?.network}
      registerField={registerField}
      removeSelectedBrowseGroup={removeSelectedBrowseGroup}
      selectedFields={legacyFields?.service_case?.oonCase?.selected || []}
      onServiceTypeChangeFetchEmpty={onServiceTypeChangeFetchEmpty}
      toggleBrowse={toggleBrowse}
    />
  );
};

export const ServiceTypeAndWorkerSection = ({
  canPaginateNetworkGroups,
  contact,
  debouncedFetchGroupsUsers,
  debouncedSearchNetworkGroups,
  defaultProgramSelected,
  fetchingOONGroups,
  form,
  groupsUsers,
  invalidServiceType,
  legacyFields,
  onServiceTypeChange,
  onServiceTypeChangeFetchEmpty,
  oonGroups,
  originCoordinates,
  primaryWorkersReady,
  programsServiceTypes,
  registerField,
  removeSelectedBrowseGroup,
  toggleBrowse,
  user,
  values,
}) => {
  const serviceTypeValidations = [
    invalidServiceTypeValidation(invalidServiceType),
    serviceTypeRequiredValidation(invalidServiceType),
  ].filter(Boolean);

  return (
    <>
      <div className="mb-5">
        <Field
          name="service_case.service_type"
          validate={(val) => runServiceTypeValidations(serviceTypeValidations, val)}
        >
          {({ input, meta }) => (
            <CaseServiceTypeField
              defaultProgramSelected={defaultProgramSelected}
              input={input}
              meta={meta}
              values={values}
              programsServiceTypes={programsServiceTypes}
              onChange={(value) => onServiceTypeChange(value, form, values, legacyFields)}
              user={user}
            />
          )}
        </Field>
      </div>

      <OONGroupsSection
        canPaginateNetworkGroups={canPaginateNetworkGroups}
        contact={contact}
        debouncedSearchNetworkGroups={debouncedSearchNetworkGroups}
        defaultProgramSelected={defaultProgramSelected}
        fetchingOONGroups={fetchingOONGroups}
        legacyFields={legacyFields}
        onServiceTypeChangeFetchEmpty={onServiceTypeChangeFetchEmpty}
        oonGroups={oonGroups}
        originCoordinates={originCoordinates}
        registerField={registerField}
        removeSelectedBrowseGroup={removeSelectedBrowseGroup}
        toggleBrowse={toggleBrowse}
        values={values}
      />

      <div className="mb-5">
        <Field
          name="service_case.primary_worker_id"
          validate={(val) => validations.isRequired(val)}
        >
          {({ input, meta }) => (
            <CasePrimaryWorkerField
              disabled={!values?.program || !primaryWorkersReady}
              fetchGroupsUsers={debouncedFetchGroupsUsers}
              field={input}
              meta={meta}
              groupsUsers={groupsUsers}
            />
          )}
        </Field>
      </div>
    </>
  );
};

const noEmptySpaces = (value) => (value && value.trim() === '') && 'Required';
const composeValidators = (...validators) => (value) => validators.reduce(
  (error, validator) => error || validator(value),
  undefined,
);

export const DescriptionSection = ({
  defaultProgramSelected,
  groupIsSensitive,
}) => (
  <div className="mb-5">
    <Field
      name="service_case.description"
      validate={composeValidators((val) => validations.isRequired(val), noEmptySpaces)}
    >
      {({ input, meta }) => (
        <TextField
          input={input}
          meta={meta}
          id="description"
          label={defaultProgramSelected ? 'Notes' : 'Case Description'}
          labelClassName="uppercase"
          className="resize"
          afterLabelContent={(
            <InfoPanel
              className="add-case-details-form__info-panel"
              message={
                groupIsSensitive ?
                  [REQUEST_SERVICE_INFO_MESSAGE, CFR_PART_2_CASE_MESSAGE].join(' ') :
                  REQUEST_SERVICE_INFO_MESSAGE
              }
            />
          )}
          inline={false}
          required
        />
      )}
    </Field>
  </div>
);

export const PersonConditionSection = ({
  personConditions,
  validateClientNeedsField,
}) => {
  const uniqueOptions = _.uniqBy(personConditions, 'value');

  return (
    <>
      <div className="mb-2">
        {"Select the identified need from the client's screening or assessment that relates to this case."}
      </div>
      <Field
        name="client_needs"
        validate={(val) => validateClientNeedsField(val)}
      >
        {({ input, meta }) => (
          <MultiSelectField
            id="case-client-need"
            label="Associated Needs"
            options={uniqueOptions}
            value={input.value || []}
            onChange={input.onChange}
            onBlur={input.onBlur}
            required
            clearable
            placeholder="Pick One or More"
            error={(meta.touched || meta.submitError) && meta.error}
          />
        )}
      </Field>
    </>
  );
};

CaseTypeAndNetworkSection.propTypes = {
  disableOnEmptyCaseType: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  values: PropTypes.object,
  group: PropTypes.object.isRequired,
  hasGroupLicense: PropTypes.bool.isRequired,
  hasNetworkLicense: PropTypes.bool.isRequired,
  networks: PropTypes.array.isRequired,
  onCaseTypeChange: PropTypes.func.isRequired,
  onNetworkChange: PropTypes.func.isRequired,
};

CaseTypeAndNetworkSection.defaultProps = {
  values: undefined,
};

ProgramSection.propTypes = {
  disableOnEmptyCaseType: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  values: PropTypes.object,
  groupId: PropTypes.string.isRequired,
  hasGroupLicense: PropTypes.bool.isRequired,
  isCoordinationGroup: PropTypes.bool.isRequired,
  programmingOptionsArray: PropTypes.array.isRequired,
  onProgramChange: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  caseProgramEnrollment: PropTypes.object.isRequired,
  isCheckingProgramEnrollment: PropTypes.bool.isRequired,
  registerField: PropTypes.func.isRequired,
};

ProgramSection.defaultProps = {
  values: undefined,
};

OONGroupsSection.propTypes = {
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  contact: PropTypes.object,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  defaultProgramSelected: PropTypes.bool.isRequired,
  fetchingOONGroups: PropTypes.bool.isRequired,
  legacyFields: PropTypes.object,
  onServiceTypeChangeFetchEmpty: PropTypes.bool.isRequired,
  oonGroups: PropTypes.array.isRequired,
  originCoordinates: PropTypes.array.isRequired,
  registerField: PropTypes.func.isRequired,
  removeSelectedBrowseGroup: PropTypes.func.isRequired,
  toggleBrowse: PropTypes.func.isRequired,
  values: PropTypes.object,
};

OONGroupsSection.defaultProps = {
  contact: {},
  legacyFields: undefined,
  values: undefined,
};

ServiceTypeAndWorkerSection.propTypes = {
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  contact: PropTypes.object,
  debouncedFetchGroupsUsers: PropTypes.func.isRequired,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  defaultProgramSelected: PropTypes.bool.isRequired,
  fetchingOONGroups: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  groupsUsers: PropTypes.object.isRequired,
  invalidServiceType: PropTypes.object,
  legacyFields: PropTypes.object,
  onServiceTypeChange: PropTypes.func.isRequired,
  onServiceTypeChangeFetchEmpty: PropTypes.bool.isRequired,
  oonGroups: PropTypes.array.isRequired,
  originCoordinates: PropTypes.array.isRequired,
  primaryWorkersReady: PropTypes.bool.isRequired,
  programsServiceTypes: PropTypes.object.isRequired,
  registerField: PropTypes.func.isRequired,
  removeSelectedBrowseGroup: PropTypes.func.isRequired,
  toggleBrowse: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  values: PropTypes.object,
};

ServiceTypeAndWorkerSection.defaultProps = {
  contact: {},
  invalidServiceType: null,
  legacyFields: undefined,
  values: undefined,
};

DescriptionSection.propTypes = {
  defaultProgramSelected: PropTypes.bool.isRequired,
  groupIsSensitive: PropTypes.bool.isRequired,
};

PersonConditionSection.propTypes = {
  personConditions: PropTypes.array.isRequired,
  validateClientNeedsField: PropTypes.func.isRequired,
};
